import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckCircle, ArrowLeft, History } from "lucide-react";

interface WithdrawDetails {
  transactionHash: string;
  token?: string;
  amount?: number;
  toAddress?: string;
  network?: string;
  timestamp?: string;
  status?: string;
  transactionId?: string;
}

export default function WithdrawSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const [details, setDetails] = useState<WithdrawDetails | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuthAndLoadDetails = () => {
      console.log("[WithdrawSuccess] Checking authentication...");
      
      // Check sessionStorage for user
      const userDataStr = sessionStorage.getItem("userData");
      const userEmail = sessionStorage.getItem("userEmail");
      
      if (!userDataStr && !userEmail) {
        console.log("[WithdrawSuccess] No user found in sessionStorage, redirecting to auth");
        navigate("/auth");
        return;
      }

      // Get transaction details from location state
      const state = location.state as any;
      if (state?.details) {
        console.log("[WithdrawSuccess] Transaction details found:", state.details);
        setDetails(state.details);
      } else {
        console.log("[WithdrawSuccess] No transaction details found, redirecting to dashboard");
        navigate("/dashboard");
      }
      
      setLoading(false);
    };

    checkAuthAndLoadDetails();
  }, [location.state, navigate]);

  const goToHistory = () => {
    navigate("/history");
  };

  const goToDashboard = () => {
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background text-foreground px-4 py-6">
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <button
              onClick={goToDashboard}
              className="p-2 hover:bg-accent rounded-lg transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="text-2xl font-bold">LOADING...</h1>
          </div>
          <div className="flex justify-center items-center h-64">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!details) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-6">
      <div className="max-w-md mx-auto">
        {/* Header with Back Button */}
        <div className="flex items-center gap-3 mb-8">
          <button
            onClick={goToDashboard}
            className="p-2 hover:bg-accent rounded-lg transition"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-2xl font-bold">WITHDRAW SUCCESS</h1>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Tick Mark */}
          <div className="flex justify-center">
            <CheckCircle className="w-16 h-16 text-green-500" />
          </div>

          {/* Transaction Completed Text */}
          <div className="text-center">
            <h2 className="text-xl font-semibold">TRANSACTION IS COMPLETED</h2>
          </div>

          {/* Transaction Details (optional - can show if available) */}
          {details.transactionHash && details.transactionHash !== "pending" && (
            <div className="bg-card/50 p-4 rounded-lg space-y-2">
              <p className="text-xs text-muted-foreground">Transaction Hash:</p>
              <p className="text-xs font-mono break-all">{details.transactionHash}</p>
              
              {details.amount && details.token && (
                <div className="flex justify-between pt-2">
                  <span className="text-sm text-muted-foreground">Amount:</span>
                  <span className="text-sm font-semibold">{details.amount} {details.token}</span>
                </div>
              )}
            </div>
          )}

          {/* Check History Button - Capital text, no icon */}
          <Button
            onClick={goToHistory}
            className="w-full py-6 text-lg font-semibold"
            variant="outline"
          >
            CHECK HISTORY
          </Button>

          {/* Back to Dashboard Button */}
          <Button
            onClick={goToDashboard}
            className="w-full py-6 text-lg font-semibold"
            variant="ghost"
          >
            BACK TO DASHBOARD
          </Button>
        </div>
      </div>
    </div>
  );
}
